import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { RootState } from "../../common/state/store";
import {
  duplicateProjectGroup,
  fetchAllCurrentElements,
  getEditedProjectGroup,
  getNewProjectGroup,
  getSelectedProjectGroup,
  setCurrentElementsFromCurrentProjectGroup,
  setEditedThumbnail,
  setNewProjectGroup,
  setSelectedProjectGroup,
  setSelectedProjectGroupFromId,
  updateProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { DynamicMedia } from "../../features/graph/components/DynamicField";
import {
  mediaSelector,
  setMediaSearch,
  setOriginalMediaGallery,
} from "../../features/gallery/state/gallerySlice";
import { useEffect, useState } from "react";
import type { IMedia } from "../../model/unityObject";
import { closeStandardModal, openStandardModal } from "../../common/state/slice/modal/modalSlice";
import ModalGallery from "../../features/graph/components/ModalGallery";
import { FooterButtons } from "../../common/components/FooterButtons";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { trackEvent } from "../../trackEvent";

interface ICreateProjectGroupThumbnail {
  incrementStep?: () => void;
  decrementStep?: () => void;
  mode?: "new" | "edit" | "duplicate";
}

const CreateProjectGroupThumbnail = ({
  incrementStep,
  decrementStep,
  mode,
}: ICreateProjectGroupThumbnail) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { register, handleSubmit, setValue, control } = useForm<any>();

  const mediasList = useAppSelector(mediaSelector);

  const [currentThumbnailId, setCurrentThumbnailId] = useState<number>(47); // logoWixar by default

  const { newProjectGroup, selectedProjectGroup, editedProjectGroup } = useAppSelector(
    (state: RootState) => {
      return {
        newProjectGroup: getNewProjectGroup(state),
        selectedProjectGroup: getSelectedProjectGroup(state),
        editedProjectGroup: getEditedProjectGroup(state),
      };
    },
  );

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  // Dance around the media preview issues that arose from repurposing the "EditNodeForm" component
  const getEnhancedPgSourceThumbnailField = () => {
    const enhancedField = {
      type: "media",
      mediaType: "image",
      fieldName: "source_thumbnail",
      currentValue: currentThumbnailId,
      relevantSourceMedia: mediasList.find(
        (m: IMedia) => Number(m.id) === Number(currentThumbnailId),
      ),
    };
    if (!currentThumbnailId) {
      enhancedField.currentValue = -1;
      enhancedField.relevantSourceMedia = mediasList.find(
        (m: IMedia) => Number(m.id) === Number(currentThumbnailId),
      );
    }
    return enhancedField;
  };

  const openGallery = () => {
    dispatch(
      setMediaSearch({
        mediaType: "image",
        media360tag: "",
      }),
    );
    dispatch(setOriginalMediaGallery(currentThumbnailId));
    dispatch(openStandardModal("modal-gallery"));
  };

  const onSubmit = () => {
    // Form submission handled by handleSave via FooterButtons
  };

  // Callback from ModalGallery selection
  const onMediaChange = (selectedMedia: IMedia) => {
    dispatch(closeStandardModal("modal-gallery"));
    setCurrentThumbnailId(Number(selectedMedia.id));
  };

  useEffect(() => {
    let tbnId = null;
    switch (mode) {
      case "edit":
        tbnId = Number(editedProjectGroup.source_thumbnail.id);
        setValue("thumbnailId", tbnId);
        setCurrentThumbnailId(tbnId);
        break;
      case "duplicate":
        tbnId = newProjectGroup.thumbnailId;
        setValue("thumbnailId", tbnId);
        setCurrentThumbnailId(tbnId);
        break;
      case "new":
      default:
        tbnId = newProjectGroup.thumbnailId;
        setValue("thumbnailId", tbnId);
        setCurrentThumbnailId(tbnId);
        break;
    }
  }, [editedProjectGroup.source_thumbnail]);

  const enhancedField = getEnhancedPgSourceThumbnailField();

  const handleSave = () => {
    if (mode === "edit") {
      void dispatch(
        updateProjectGroup({
          id: selectedProjectGroup.id,
          payload: {
            source_thumbnail: enhancedField.relevantSourceMedia,
          },
          componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(setAlert({ msg: t("alerts.projectGroupUpdateSuccess"), type: "success" }));
          dispatch(setEditedThumbnail(enhancedField.relevantSourceMedia as IMedia));
        });
      dispatch(setCurrentElementsFromCurrentProjectGroup());
      navigate(`/courses/${selectedProjectGroup.id}/list`);
    } else if (mode === "duplicate") {
      trackEvent("pg-duplicate");
      dispatch(
        duplicateProjectGroup({
          projectGroup: selectedProjectGroup.id,
          thumbnail: currentThumbnailId,
          newName: newProjectGroup.newName,
          componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
        }),
      )
        .unwrap()
        .then((res) => {
          const newProjectGroupId = res?.data?.duplicatedProjectGroup?.id;
          if (!newProjectGroupId) {
            dispatch(setAlert({ msg: t("alerts.projectGroupUpdateFail"), type: "warning" }));
            return;
          }
          dispatch(setSelectedProjectGroupFromId(newProjectGroupId));
          dispatch(setSelectedProjectGroup(res.data.duplicatedProjectGroup));
          void dispatch(
            fetchAllCurrentElements({
              projectGroupId: newProjectGroupId,
              componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
            }),
          ).then(() => {
            navigate(`/courses/${newProjectGroupId}/list`);
            dispatch(setAlert({ msg: t("alerts.projectGroupUpdateSuccess"), type: "success" }));
          });
        })
        .catch(() => {
          dispatch(setAlert({ msg: t("alerts.projectGroupUpdateFail"), type: "warning" }));
        });
    }
  };

  const handleCancel = () => {
    navigate(`/courses/${selectedProjectGroup.id}/list`);
  };

  return (
    <>
      <IsLoading
        componentId={PROJECT_GROUP_LIST_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <h1 className="text-2xl my-5 px-6 font-medium">{t("general.selectThumbnail")}</h1>
        <ModalGallery
          nodeId={"new-project-group-tbn"}
          onMediaChange={onMediaChange}
          allowedMediaTypeOverride="image"
        />

        <form
          className="flex flex-col grow w-full justify-between"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-10 w-full my-5 px-6">
            <div className="flex flex-col items-center justify-center bg-green-200 p-6 w-full max-w-max">
              <Controller
                control={control}
                name={"source_thumbnail"}
                render={({ field }) => (
                  <DynamicMedia
                    field={field as any}
                    fieldType={enhancedField.type}
                    register={register}
                    handleButtonClick={() => {
                      openGallery();
                    }}
                    currentMedia={enhancedField.currentValue as number}
                    media={enhancedField.relevantSourceMedia as IMedia}
                  />
                )}
              />
            </div>
          </div>
          {mode === "edit" || mode === "duplicate" ? (
            <FooterButtons
              cancelCb={handleCancel}
              cancelText={t("general.cancel")}
              nextText={t("general.save")}
              nextCb={handleSave}
            />
          ) : (
            <FooterButtons
              cancelText={t("general.cancel")}
              cancelCb={() => navigate("/courses")}
              backText={t("general.back")}
              backCb={decrementStep}
              nextText={t("general.next")}
              nextCb={() => {
                dispatch(
                  setNewProjectGroup({ ...newProjectGroup, thumbnailId: currentThumbnailId }),
                );

                if (incrementStep) {
                  incrementStep();
                }
              }}
              nextDisabled={!enhancedField.relevantSourceMedia}
            />
          )}
        </form>
      </IsLoading>
    </>
  );
};

export default CreateProjectGroupThumbnail;
